import { useQuery } from '@tanstack/react-query';
import { StatusBadge } from '@/components/common/Badge';
import { Card, CardHeader } from '@/components/common/Card';
import { ErrorState, LoadingState } from '@/components/common/States';
import { studentsApi } from '@/services/api/endpoints/students';
import { queryKeys } from '@/services/api/queryKeys';
import type { AcademicYearRecord, StudentPlacement } from '@/types/domain';
import { formatDate, humanise, orDash } from '@/utils/format';

/**
 * The sections held during one academic year.
 *
 * More than one appears when the student moved section partway through. Showing
 * every placement with its dates is the whole reason placements close rather
 * than change — a design that overwrote the section would have only the last one
 * to show, and no way to answer which class the child was in last March.
 */
function Placements({ placements }: { placements: StudentPlacement[] }) {
  if (placements.length === 0) {
    return <p className="text-xs text-subtle">No section assigned for this year.</p>;
  }

  return (
    <ol className="space-y-2">
      {placements.map((placement) => (
        <li key={placement.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
          {/* A rail rather than a bullet, so a multi-section year reads as a
              sequence the eye can follow. */}
          <span
            className={`mt-1.5 h-1.5 w-1.5 shrink-0 ${
              placement.current ? 'bg-accent' : 'bg-line-strong'
            }`}
            aria-hidden="true"
          />
          <span className="font-medium text-ink">{orDash(placement.sectionName)}</span>
          {placement.rollNumber !== undefined && (
            <span className="text-xs text-subtle">#{placement.rollNumber}</span>
          )}
          <span className="text-xs text-muted">
            {formatDate(placement.validFrom)} –{' '}
            {placement.validTo === undefined ? 'present' : formatDate(placement.validTo)}
          </span>
          {placement.endReason !== undefined && (
            <span className="text-xs text-subtle">({humanise(placement.endReason)})</span>
          )}
        </li>
      ))}
    </ol>
  );
}

function YearRow({ year }: { year: AcademicYearRecord }) {
  return (
    <li className="px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-ink">
            {orDash(year.academicYearName)}
            <span className="ml-2 font-normal text-muted">{orDash(year.gradeName)}</span>
          </p>
          <p className="mt-0.5 text-xs text-subtle">
            {orDash(year.campusName)} · enrolled {formatDate(year.enrollmentDate)}
            {year.completionDate !== undefined && ` · ended ${formatDate(year.completionDate)}`}
          </p>
        </div>
        <StatusBadge status={year.status} />
      </div>

      <div className="mt-3 pl-1">
        <Placements placements={year.placements} />
      </div>

      {year.continuedFromEnrollmentId !== undefined && (
        <p className="mt-2 text-xs text-subtle">Continued from the previous year’s enrolment.</p>
      )}
      {year.remarks !== undefined && <p className="mt-2 text-xs text-muted">{year.remarks}</p>}
    </li>
  );
}

/**
 * A student's whole career, newest year first.
 *
 * Nothing here is derived or reconstructed: each year is a real enrolment row
 * that was closed rather than overwritten when the next one began.
 */
export function AcademicHistoryPanel({ studentId }: { studentId: string }) {
  const query = useQuery({
    queryKey: queryKeys.studentProfile.academicHistory(studentId),
    queryFn: () => studentsApi.academicHistory(studentId),
    enabled: studentId !== '',
  });

  return (
    <Card>
      <CardHeader
        title="Academic history"
        description="Every year, and every section held within each year."
      />

      {query.isPending ? (
        <LoadingState label="Loading history" />
      ) : query.error !== null ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data === undefined || query.data.years.length === 0 ? (
        <p className="py-6 text-center text-sm text-subtle">
          Not enrolled for any academic year yet.
        </p>
      ) : (
        <ol className="divide-y divide-line">
          {query.data.years.map((year) => (
            <YearRow key={year.enrollmentId} year={year} />
          ))}
        </ol>
      )}
    </Card>
  );
}
